// The pull request as last read, kept in this browser, so that opening or
// reloading its page shows it at once, as of then, while it is read again
// (pr/program.ts, `pr-kept`): the page no longer waits on GitHub's answer
// before it shows anything.
//
// It is a runtime cache (runtime/cache.ts) in the signed-in account's
// IndexedDB, one record per pull request (`prKeyOf`), KEPT_PRS at most
// (the least recently written go first), whose key names the server's
// origin, the account, the layout of its records (KEPT_PR_SCHEMA) and an
// inputs hash of what they are read from (KEPT_PR_INPUTS: every query text
// and the layout `readKeptPr` decodes). A record is read back only under
// the same key, so a deploy never shows a copy of the old shape, and one
// is decoded as GitHub's answers are (api/answers.ts): what cannot be read
// is a miss.
//
// Only the pull request as GitHub sent it is kept: never the viewer's
// drafts, marks or attention overrides (their own stores), nor the first
// page of "Viewed" states (the sync's own record). A record names its pull
// request as its subject, so the account's invalidation log drops it once
// the pull request changed after its read began: the app wrote to it, in
// this tab or another, before or after this page opened, or a notice named
// it. What the viewer did in Changeling is never shown as it was before.
// The viewer opening the pull request does not drop it (`opened: false`):
// the copy is what that opening reads again, and is shown only until it
// has. Without the log nothing is kept or shown: it could not vouch for a
// record.
//
// A copy is never a read: it confirms none of the store's pending writes
// (pr/program.ts gives no `settle` for it), only a real read does.
import { identityUser } from '../../api/identity';
import * as queries from '../../api/queries';
import { KEPT_PR_LAYOUT, readKeptPr } from '../../api/answers';
import { cacheKey, hashInputs, LOG_KEPT_MS, openCache, type Backend, type InputsHash, type InvalidationLog, type WriteResult } from '../../runtime/cache';
import { prKeyOf } from '../../runtime/appLog';
import { idbBackend } from '../../runtime/idb';
import { accountLog } from '../dashCache';
import type { LoadedPr, PrAt } from './fetch';

/** The layout of the kept records; part of the cache key. */
export const KEPT_PR_SCHEMA = 1;

/** How many pull requests are kept, the least recently read going first. */
export const KEPT_PRS = 50;

/**
 * What the kept copies are read from: every query text api/queries.ts
 * exports (by name, so a query added there is covered without a list to
 * keep), and the layout they are decoded by.
 */
export function keptPrInputs(): string[] {
  const texts = Object.entries(queries)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([, v]) => String(v));
  return [...texts, `layout ${KEPT_PR_LAYOUT}`];
}

export const KEPT_PR_INPUTS: InputsHash = hashInputs(...keptPrInputs());

/** A read of the pull request kept from before, and when that read began (epoch ms). */
export interface KeptPr {
  loaded: LoadedPr;
  at: number;
}

/** What is stored: a whole read, without its "Viewed" states. */
type Kept = Omit<LoadedPr, 'viewed'>;

/** The kept reads of one account (see the file comment). Reads that fail are misses. */
export interface PrCopies {
  /** The read last kept of the pull request `at`, or null. */
  read(at: PrAt): Promise<KeptPr | null>;
  /**
   * Keep `loaded`, a whole read of the pull request `at` that began at
   * `basis` (epoch ms); null (GitHub does not show it) drops what is kept.
   * A read begun before the pull request changed is not kept.
   */
  write(at: PrAt, loaded: LoadedPr | null, basis: number): Promise<WriteResult>;
  /** Let go of the lease on the records (another account signed in). */
  close(): void;
}

/** The kept reads for `opts.account` at `opts.origin`, dropped by `opts.log`'s events. */
export function prCopies(opts: { origin: string; account: string; log: InvalidationLog; backend?: Backend; now?: () => number }): PrCopies {
  const cache = openCache<Kept>({
    name: 'pr-last-read',
    key: cacheKey({ origin: opts.origin, account: opts.account, schemaVersion: KEPT_PR_SCHEMA, inputsHash: KEPT_PR_INPUTS }),
    decode: readKeptPr,
    backend: opts.backend ?? idbBackend(),
    // Past the log's horizon nothing vouches for a record.
    maxAgeMs: LOG_KEPT_MS,
    limit: KEPT_PRS,
    log: opts.log,
    subjects: (k) => [k],
    opened: false,
    now: opts.now,
  });
  let release: (() => void) | null = null;
  const hold = () => {
    if (release) return;
    release = cache.hold();
    void cache.gc();
  };
  const keyOf = (at: PrAt) => prKeyOf(`${at.owner}/${at.repo}`, at.number);
  return {
    async read(at) {
      hold();
      const e = await cache.get(keyOf(at));
      // Another pull request's read under this one's key is no copy of it.
      if (!e || e.value.pr.number !== at.number) return null;
      return { loaded: { ...e.value, viewed: null }, at: e.basis };
    },
    write(at, loaded, basis) {
      hold();
      const kept: Kept | null = loaded && { viewer: loaded.viewer, repository: loaded.repository, pr: loaded.pr, threads: loaded.threads, timeline: loaded.timeline, truncated: loaded.truncated };
      return cache.write({ [keyOf(at)]: kept }, cache.ticketAt(basis));
    },
    close() {
      release?.();
      release = null;
    },
  };
}

let shared: { account: string; copies: Promise<PrCopies | null> } | null = null;

/**
 * The kept reads of the account signed in now, once its invalidation log
 * is open; null when nobody is signed in, or the log could not be opened.
 * Read when a pull request's store opens: the account it names is the one
 * whose copies it shows and keeps.
 */
export function accountPrCopies(): Promise<PrCopies | null> {
  const account = identityUser();
  if (!account || typeof location === 'undefined') return Promise.resolve(null);
  if (shared?.account !== account) {
    void shared?.copies.then((c) => c?.close());
    const origin = location.origin;
    // Made now, for the account signed in now: after a switch it reads nothing and refuses writes (runtime/idb.ts).
    const backend = idbBackend();
    shared = { account, copies: accountLog(origin).then((log) => (log && log.account === account ? prCopies({ origin, account, log, backend }) : null)) };
  }
  return shared.copies;
}
